import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AtSign, Check, MapPin, Search, User, X } from "lucide-react";
import { api, type Cadence, type Recipient, type ScheduleBody, type ScheduleDetail, type ScheduleFormat } from "../../lib/api";
import { cn, useDebouncedValue } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input, Label, Select } from "../../components/ui/primitives";
import { presetLabel, type TimePreset } from "../../lib/time";
import { defaultPreset, describeCadence, hourLabel, localTimeZone, ordinal, SCHEDULE_PRESETS, timeZones, WEEKDAYS } from "./cadence";

/** The worker's limits (schedules.ts). */
const MAX_LOCATIONS = 50;
const MAX_RECIPIENTS = 50;

export type Place = { id: string; name: string };

const FORMAT_HINTS: Record<ScheduleFormat, string> = {
  pdf: "The dashboard as it looks, on landscape pages. Long tables show the rows that fit.",
  "pdf+xlsx": "The dashboard as a PDF, and an Excel file with every row (one sheet per widget; single numbers are left out).",
  xlsx: "One sheet per widget. Single-number (KPI) widgets are left out.",
  csv: "One CSV per widget, zipped when there's more than one. Single-number (KPI) widgets are left out.",
};

/**
 * Create or edit one scheduled report email for a dashboard: what goes in it, when, for which
 * locations and to whom. The worker checks every location and recipient again on save and on
 * every send; this only offers likely choices.
 */
export function ScheduleForm({
  dashboardId, schedule, duplicate, initialLocations, staffSession, onDone, onCancel,
}: {
  dashboardId: string;
  /** Editing this one; otherwise a new schedule. */
  schedule?: ScheduleDetail;
  /** A new schedule starting from `schedule`'s settings, which stays as it is. */
  duplicate?: boolean;
  /** A new schedule's locations: what the dashboard is showing. */
  initialLocations?: Place[];
  /** Signed in through cf_sso, so any location can be searched for. */
  staffSession: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  const [widgetIds, setWidgetIds] = useState<string[] | null>(schedule?.widgetIds ?? null);
  const [format, setFormat] = useState<ScheduleBody["format"]>(schedule?.format ?? "xlsx");
  const [cadence, setCadence] = useState<Cadence>(schedule?.cadence ?? { kind: "daily", hour: 7 });
  const [timezone, setTimezone] = useState(schedule?.timezone ?? localTimeZone());
  // Follows the cadence (daily → yesterday, ...) until the owner picks a range themselves.
  const [picked, setPicked] = useState<TimePreset | null>(schedule?.timePreset ?? null);
  const timePreset = picked ?? defaultPreset(cadence);
  const [locations, setLocations] = useState<Place[]>(schedule?.locations ?? initialLocations ?? []);
  const [people, setPeople] = useState<Recipient[]>(schedule?.people ?? []);

  const detail = useQuery({ queryKey: ["dashboard", dashboardId], queryFn: () => api.dashboard(dashboardId) });
  const widgets = (detail.data?.dashboard.items ?? []).flatMap((i) => {
    const w = detail.data!.widgets[i.widgetId];
    return w ? [{ id: w.id, title: w.definition.title || "Untitled", kpi: w.definition.visualization.type === "kpi" }] : [];
  });

  const body = (): ScheduleBody => ({
    dashboardId, widgetIds, format, cadence, timezone, timePreset, locationIds: locations.map((l) => l.id), recipients: people.map((p) => p.id),
  });
  const save = useMutation({
    mutationFn: () => (schedule && !duplicate ? api.updateSchedule(schedule.id, body()) : api.createSchedule(body())),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["schedules"] });
      if (schedule && !duplicate) void qc.invalidateQueries({ queryKey: ["schedule", schedule.id] });
      onDone();
    },
  });

  const toggleWidget = (id: string) => setWidgetIds((cur) => (cur?.includes(id) ? cur.filter((x) => x !== id) : [...(cur ?? []), id]));
  const missing = !locations.length ? "Choose at least one location" : !people.length ? "Choose at least one recipient"
    : widgetIds && !widgetIds.length ? "Choose at least one widget" : undefined;

  return (
    <>
      <div className="space-y-5">
        <section>
          <Label>Content</Label>
          <div className="mt-1.5 flex flex-wrap gap-2">
            <Segmented
              value={widgetIds === null ? "all" : "some"}
              onChange={(v) => setWidgetIds(v === "all" ? null : [])}
              options={[["all", "Whole dashboard"], ["some", "Chosen widgets"]]}
              label="Content"
            />
            <Segmented value={format} onChange={setFormat} options={[["pdf", "PDF"], ["pdf+xlsx", "PDF + Excel"], ["xlsx", "Excel"], ["csv", "CSV"]]} label="Format" />
          </div>
          <p className="mt-1 text-xs text-subtle">
            {FORMAT_HINTS[format]}
          </p>
          {widgetIds !== null && (
            <div className="mt-1.5 max-h-40 overflow-y-auto">
              {widgets.map((w) => (
                <CheckRow key={w.id} checked={widgetIds.includes(w.id)} disabled={w.kpi} onClick={() => toggleWidget(w.id)}>
                  {w.title}
                  {w.kpi && <span className="ml-1.5 text-xs text-subtle">single number</span>}
                </CheckRow>
              ))}
            </div>
          )}
        </section>

        <section>
          <Label>When</Label>
          <CadencePicker cadence={cadence} onChange={setCadence} timezone={timezone} onTimezoneChange={setTimezone} />
          <p className="mt-1 text-xs text-subtle">
            {describeCadence(cadence)}, {timezone.replace(/_/g, " ")} time.
          </p>
        </section>

        <section>
          <Label htmlFor="schedule-range">Date range</Label>
          <Select id="schedule-range" value={timePreset} onChange={(e) => setPicked(e.target.value as TimePreset)} className="mt-1.5 w-48">
            {SCHEDULE_PRESETS.map((p) => <option key={p} value={p}>{presetLabel(p)}</option>)}
          </Select>
          <p className="mt-1 text-xs text-subtle">
            Worked out on each send from each location's business day, whatever dates the dashboard is showing.
            {timePreset === "last_week" && " Last week is Monday to Sunday."}
          </p>
        </section>

        <LocationsPicker locations={locations} onChange={setLocations} staffSession={staffSession} hint="Saved with the schedule and checked again on every send." />
        <RecipientsPicker people={people} onChange={setPeople} locationIds={locations.map((l) => l.id)} />
      </div>

      {save.error && <p className="mt-4 text-sm text-bad">{save.error.message}</p>}
      <div className="mt-6 flex items-center justify-end gap-2">
        {missing && <span className="mr-auto text-xs text-subtle">{missing}</span>}
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" disabled={!!missing || save.isPending} onClick={() => save.mutate()}>
          {schedule && !duplicate ? "Save" : "Schedule"}
        </Button>
      </div>
    </>
  );
}

function CadencePicker({ cadence, onChange, timezone, onTimezoneChange }: {
  cadence: Cadence; onChange: (c: Cadence) => void; timezone: string; onTimezoneChange: (tz: string) => void;
}) {
  const setKind = (kind: Cadence["kind"]) =>
    onChange(kind === "daily" ? { kind, hour: cadence.hour } : kind === "weekly" ? { kind, weekday: 1, hour: cadence.hour } : { kind, day: 1, hour: cadence.hour });
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      <Select value={cadence.kind} onChange={(e) => setKind(e.target.value as Cadence["kind"])} aria-label="How often" className="w-28">
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
        <option value="monthly">Monthly</option>
      </Select>
      {cadence.kind === "weekly" && (
        <Select value={cadence.weekday} onChange={(e) => onChange({ ...cadence, weekday: Number(e.target.value) })} aria-label="Day of the week" className="w-32">
          {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </Select>
      )}
      {cadence.kind === "monthly" && (
        <Select value={cadence.day} onChange={(e) => onChange({ ...cadence, day: Number(e.target.value) })} aria-label="Day of the month" className="w-24">
          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{ordinal(d)}</option>)}
        </Select>
      )}
      <span className="text-sm text-muted">at</span>
      <Select value={cadence.hour} onChange={(e) => onChange({ ...cadence, hour: Number(e.target.value) })} aria-label="Hour" className="w-24">
        {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
      </Select>
      <Select value={timezone} onChange={(e) => onTimezoneChange(e.target.value)} aria-label="Time zone" className="min-w-0 flex-1 basis-44">
        {timeZones(timezone).map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
      </Select>
    </div>
  );
}

/** The session's locations to tick, and (staff signed in through cf_sso) a search for any other. */
export function LocationsPicker({ locations, onChange, staffSession, hint }: {
  locations: Place[]; onChange: (l: Place[]) => void; staffSession: boolean; hint: string;
}) {
  const [q, setQ] = useState("");
  const debounced = useDebouncedValue(q.trim(), 250);
  const session = useQuery({ queryKey: ["locations"], queryFn: api.locations });
  const found = useQuery({
    queryKey: ["picker-locations", "staff", debounced],
    queryFn: () => api.pickerLocations("staff", debounced),
    enabled: staffSession && debounced.length >= 2,
    retry: false,
  });
  const has = (id: string) => locations.some((l) => l.id === id);
  const toggle = (l: Place) =>
    onChange(has(l.id) ? locations.filter((x) => x.id !== l.id) : locations.length < MAX_LOCATIONS ? [...locations, l] : locations);
  const options: Place[] = debounced.length >= 2 && found.data
    ? found.data.map((l) => ({ id: l.id, name: l.name }))
    : (session.data ?? []).map((l) => ({ id: String(l.id), name: String(l.name) }));

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <Label>Locations</Label>
        <span className="text-xs text-subtle">{locations.length}/{MAX_LOCATIONS}</span>
      </div>
      <p className="mt-0.5 text-xs text-subtle">{hint}</p>
      {staffSession && (
        <div className="relative mt-1.5">
          <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-subtle" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find another location by name or ID" className="pl-8" />
        </div>
      )}
      <div className="mt-1 max-h-36 overflow-y-auto">
        {options.map((l) => (
          <CheckRow key={l.id} checked={has(l.id)} onClick={() => toggle(l)} icon={<MapPin className="size-3.5 text-subtle" />}>
            {l.name}
            <span className="ml-1.5 text-xs text-subtle">#{l.id}</span>
          </CheckRow>
        ))}
        {found.isSuccess && debounced.length >= 2 && !found.data.length && <p className="px-2 py-2 text-sm text-subtle">No matching locations.</p>}
        {found.error && <p className="px-2 py-2 text-sm text-bad">{found.error.message}</p>}
      </div>
      <Chips items={locations} label={(l) => l.name} onRemove={toggle} />
    </section>
  );
}

/** Staff, customers whose last sign-in covered every chosen location, and contacts granted all of them. */
function RecipientsPicker({ people, onChange, locationIds }: { people: Recipient[]; onChange: (p: Recipient[]) => void; locationIds: string[] }) {
  const [q, setQ] = useState("");
  const debounced = useDebouncedValue(q.trim(), 250);
  const found = useQuery({
    queryKey: ["schedule-recipients", debounced, locationIds],
    queryFn: () => api.scheduleRecipients(debounced, locationIds),
    enabled: locationIds.length > 0,
  });
  const has = (id: string) => people.some((p) => p.id === id);
  const toggle = (p: Recipient) =>
    onChange(has(p.id) ? people.filter((x) => x.id !== p.id) : people.length < MAX_RECIPIENTS ? [...people, p] : people);

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <Label>Recipients</Label>
        <span className="text-xs text-subtle">{people.length}/{MAX_RECIPIENTS}</span>
      </div>
      <p className="mt-0.5 text-xs text-subtle">
        GoTab staff, people who can see every chosen location, and <Link to="/contacts" className="underline hover:text-foreground">external contacts</Link> granted all of them.
        They're Bcc'd, so they don't see each other.
      </p>
      <div className="relative mt-1.5">
        <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-subtle" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pl-8" disabled={!locationIds.length} />
      </div>
      <div className="mt-1 max-h-36 overflow-y-auto">
        {!locationIds.length && <p className="px-2 py-2 text-sm text-subtle">Choose locations first.</p>}
        {found.data?.map((p) => (
          <CheckRow key={p.id} checked={has(p.id)} onClick={() => toggle(p)} icon={p.contact ? <AtSign className="size-3.5 text-subtle" /> : <User className="size-3.5 text-subtle" />}>
            {p.name}
            <span className="ml-1.5 text-xs text-subtle">{p.email}{p.staff && " · staff"}{p.contact && " · external contact"}</span>
          </CheckRow>
        ))}
        {found.isSuccess && !found.data.length && <p className="px-2 py-2 text-sm text-subtle">No one matches who can see all of these locations.</p>}
        {found.error && <p className="px-2 py-2 text-sm text-bad">{found.error.message}</p>}
      </div>
      <Chips items={people} label={(p) => p.name} onRemove={toggle} />
    </section>
  );
}

function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: [T, string][]; label: string }) {
  return (
    <div className="flex rounded-lg border border-border p-0.5" role="group" aria-label={label}>
      {options.map(([v, text]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          aria-pressed={value === v}
          className={cn("rounded-md px-2.5 py-1 text-xs font-medium text-muted hover:text-foreground", value === v && "bg-surface-2 text-foreground")}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

export function Chips<T extends { id: string }>({ items, label, onRemove }: { items: T[]; label: (t: T) => string; onRemove: (t: T) => void }) {
  if (!items.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {items.map((t) => (
        <span key={t.id} className="inline-flex items-center gap-1 rounded-md bg-accent-soft px-2 py-1 text-xs text-accent">
          {label(t)}
          <button onClick={() => onRemove(t)} aria-label={`Remove ${label(t)}`}><X className="size-3" /></button>
        </span>
      ))}
    </div>
  );
}

export function CheckRow({ checked, disabled, onClick, icon, children }: { checked: boolean; disabled?: boolean; onClick: () => void; icon?: ReactNode; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
    >
      <span className={cn("flex size-4 shrink-0 items-center justify-center rounded border border-border-strong", checked && "border-accent bg-accent text-white")}>
        {checked && <Check className="size-3" />}
      </span>
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  );
}
